/** @jsxImportSource preact */
import DOMPurify, { type Config } from "dompurify";
import { marked } from "marked";
import { useMemo } from "preact/hooks";

const SANITIZE: Config = {
  ALLOWED_TAGS: [
    "p",
    "br",
    "strong",
    "em",
    "del",
    "a",
    "ul",
    "ol",
    "li",
    "blockquote",
    "pre",
    "code",
    "hr",
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "table",
    "thead",
    "tbody",
    "tr",
    "th",
    "td",
  ],
  ALLOWED_ATTR: ["href", "title", "start", "align"],
  ALLOW_DATA_ATTR: false,
  ALLOW_ARIA_ATTR: false,
};

// 开新标签页的钩子挂在单独的实例上：挂到默认实例，店面买家点正文链接也会跳出新标签页。
const newTabPurify = DOMPurify();
newTabPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A") {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noreferrer");
  }
});

export interface MessageMarkdownProps {
  text: string;
  /** 后台回放里点链接要开新标签页，商家看完回放还在原处；店面照常在当前页跳转。 */
  linksInNewTab?: boolean;
  /** 流式展示的进度（0–1），只露出前面这一截字；不传就是全文。 */
  reveal?: number;
}

/** AI 正文只允许排版与链接；不执行 HTML，也不加载正文中的外部媒体。 */
export function MessageMarkdown({
  text,
  linksInNewTab = false,
  reveal,
}: MessageMarkdownProps) {
  const html = useMemo(
    () =>
      (linksInNewTab ? newTabPurify : DOMPurify).sanitize(
        marked.parse(text, { async: false, breaks: true }),
        SANITIZE,
      ),
    [linksInNewTab, text],
  );
  return (
    <div
      class="gn-markdown"
      dangerouslySetInnerHTML={{
        __html: reveal === undefined ? html : truncateHtml(html, reveal),
      }}
    />
  );
}

/** 去掉 Markdown 标记只留文字，净化规则和聊天窗里一致。 */
export function readPlainText(text: string): string {
  const template = document.createElement("template");
  // 不开 breaks：单个换行要留成空白，否则前后两行的字会粘在一起。
  template.innerHTML = DOMPurify.sanitize(
    marked.parse(text, { async: false }),
    SANITIZE,
  );
  return template.content.textContent.replace(/\s+/g, " ").trim();
}

/**
 * 截的是解析、净化之后的结果，不是 Markdown 原文：截原文会在流到一半时露出 `**`、链接地址和表格竖线。
 * 截断点之后的节点整个去掉，列表和表格不会先冒出空壳。
 */
function truncateHtml(html: string, reveal: number): string {
  const template = document.createElement("template");
  template.innerHTML = html;
  const root = template.content;
  let left = Math.ceil(root.textContent.length * reveal);
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const text = node.textContent ?? "";
    if (text.length < left) {
      left -= text.length;
      continue;
    }
    node.textContent = text.slice(0, left);
    let kept: Node | null = node;
    while (kept && kept !== root) {
      while (kept.nextSibling) kept.nextSibling.remove();
      kept = kept.parentNode;
    }
    break;
  }
  return template.innerHTML;
}

/** 放进面板所在的 Shadow DOM，避免依赖店面或后台的全局样式。 */
export const MESSAGE_MARKDOWN_STYLES = `
.gn-markdown { white-space: normal; min-width: 0; }
.gn-markdown > :first-child { margin-top: 0; }
.gn-markdown > :last-child { margin-bottom: 0; }
.gn-markdown p, .gn-markdown ul, .gn-markdown ol, .gn-markdown blockquote,
.gn-markdown pre, .gn-markdown table { margin: .65em 0; }
.gn-markdown ul, .gn-markdown ol { padding-inline-start: 1.5em; }
.gn-markdown li + li { margin-top: .25em; }
.gn-markdown h1, .gn-markdown h2, .gn-markdown h3, .gn-markdown h4,
.gn-markdown h5, .gn-markdown h6 { font-size: 1em; line-height: inherit; margin: .8em 0 .4em; }
.gn-markdown a { color: inherit; text-decoration: underline; text-underline-offset: .15em; }
.gn-markdown a:focus-visible { outline: 2px solid currentColor; outline-offset: 2px; }
.gn-markdown blockquote { border-inline-start: 2px solid currentColor; padding-inline-start: .75em; }
.gn-markdown pre { overflow-x: auto; padding: .6em; border: 1px solid var(--gn-message-line); border-radius: .4em; }
.gn-markdown code { font-size: .9em; white-space: pre-wrap; }
.gn-markdown pre code { white-space: pre; }
.gn-markdown table { display: block; max-width: 100%; overflow-x: auto; border-collapse: collapse; }
.gn-markdown th, .gn-markdown td { white-space: nowrap; padding: .4em .6em; border: 1px solid var(--gn-message-line); text-align: start; }
.gn-markdown hr { border: 0; border-top: 1px solid var(--gn-message-line); margin: .8em 0; }
`;
